/**
 * Included platform services, and the words that keep them apart from
 * Demo Connections.
 *
 * THE PRODUCT RULE. Cluvik Managed Storage and Cluvik Managed Email are
 * INCLUDED platform services for every tenant in every environment. They are
 * not Connection rows, they are not Demo Connections, and no toggle on any
 * screen creates or removes them. A Connection existing never means a
 * feature is enabled — and these two exist for a tenant whether or not any
 * Connection does.
 *
 * Pure. The chips render from `describeManagedServices`, and every screen
 * that names these services (the Create form, the confirmation, the tenant
 * detail card) reads the same list and the same sentences from here, so the
 * three cannot describe the same tenant differently.
 */
import { sampleCredentialsAllowed } from './environmentPresentation';

/** The vocabulary word the backend reports for each managed service
 *  (`shared/constants/managed_services.py`). */
export const CLUVIK_MANAGED = 'cluvik_managed';

/** The two, in display order. Labels live here because the form has to
 *  render the chips before any server response exists. */
export const MANAGED_SERVICES = Object.freeze([
  Object.freeze({ key: 'storage', label: 'Cluvik Managed Storage' }),
  Object.freeze({ key: 'email', label: 'Cluvik Managed Email' }),
]);

/** Under the Demo Connections group, everywhere it is shown. Copy that
 *  implied a provider feature was switched on because a connection existed
 *  is the misreading this sentence exists to close. */
export const DEMO_CONNECTIONS_FEATURE_NOTE =
  'Demo connections are available to features; each feature still selects and activates its connection separately.';

/** What the OFF confirmation says, in those words. */
export const DEMO_CONNECTIONS_DETACH_CONFIRMATION =
  'Only demo-profile connections are detached. Connections your users created stay untouched.';

/** The one-line meaning of the included group. */
export const MANAGED_SERVICES_NOTE =
  'Included for every tenant in every environment. These are platform services, not connections and not demo connections.';

/**
 * The chips, from a server `managed_services` object when one is present.
 *
 * `reported` says whether THIS payload named the service; the status is
 * `included` either way, because the rule is the platform's and not the
 * payload's — an older read that omits the field has not made the service
 * go away. Only an explicit value other than `cluvik_managed` changes the
 * chip, and then it shows that value rather than pretending.
 */
export function describeManagedServices(payload) {
  const source = payload && typeof payload === 'object' ? payload : null;
  return MANAGED_SERVICES.map(({ key, label }) => {
    const value = source ? source[key] : undefined;
    const reported = value !== undefined && value !== null;
    const included = !reported || value === CLUVIK_MANAGED;
    return {
      key,
      label,
      reported,
      status: included ? 'included' : 'other',
      statusLabel: included ? 'Included' : String(value),
    };
  });
}

/**
 * Demo Connections as the create response reported them.
 *
 * Reads the `demo_connections` block first and the flat fields second, so a
 * response from before the block existed still renders the right words.
 * `environment_allowed: false` is what LIVE sends; absent, the environment
 * itself decides.
 */
export function describeCreatedDemoConnections(success) {
  const s = success || {};
  const block = s.demo_connections && typeof s.demo_connections === 'object' ? s.demo_connections : null;
  const allowed = block && typeof block.environment_allowed === 'boolean'
    ? block.environment_allowed
    : sampleCredentialsAllowed(s.environment);
  const requested = block ? block.requested === true : s.sample_credentials === true;
  const profileId = (block ? block.profile_id : s.sample_connection_profile_id) || null;

  if (!allowed) {
    return { state: 'not_allowed', label: 'Not available for Live', profileId: null };
  }
  if (!requested) {
    return { state: 'off', label: 'Off', profileId: null };
  }
  return { state: 'on', label: profileId ? `On · ${profileId}` : 'On', profileId };
}
